import { mediaTextApi, mediaTextError, mediaTextRetryAfter } from '../models/mediaTextApi';

export function createMediaTextStore(api = mediaTextApi, { debounceMs = 80, conflictDelayMs = 1500 } = {}) {
  let state = {}, generation = 0, batchTimer, active = true;
  const listeners = new Set(), checked = new Set(), pending = new Set(), running = new Map(), versions = new Map(), waits = new Map();
  const update = (id, patch) => { state = { ...state, [id]: { ...state[id], ...patch } }; listeners.forEach(listener => listener()); };
  const valid = (epoch, id, version) => epoch === generation && versions.get(id) === version;
  const accept = (id, result) => update(id, { result, status: result.status === 'completed' ? 'result' : result.status === 'pending' ? 'loading' : 'error',
    error: result.status === 'failed' ? mediaTextError({ code: result.error_code }) : null });
  const flush = async () => {
    batchTimer = null;
    const ids = [...pending]; pending.clear();
    const epoch = generation;
    for (let start = 0; start < ids.length; start += 100) {
      if (epoch !== generation) return;
      const chunk = ids.slice(start, start + 100), snapshots = chunk.map(id => versions.get(id));
      try {
        const results = await api.saved(chunk);
        for (const [index, id] of chunk.entries()) {
          if (valid(epoch, id, snapshots[index]) && results[id] && !state[id]?.result) accept(id, results[id]);
        }
      } catch { /* A saved-result read must never interrupt the chat. */ }
    }
  };
  const store = {
    activate() { active = true; },
    getSnapshot: () => state,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    check(id) {
      if (!active || !id || checked.has(id)) return;
      checked.add(id); pending.add(id);
      if (!batchTimer) batchTimer = setTimeout(flush, debounceMs);
    },
    close(id) { update(id, { open: false }); },
    open(id) { if (!active) return Promise.resolve(); update(id, { open: true }); return store.read(id); },
    read(id, force = false) {
      if (!active) return Promise.resolve();
      if (state[id]?.retryAt > Date.now()) return Promise.resolve();
      if (running.has(id)) return running.get(id);
      if (!force && state[id]?.result?.status === 'completed') return Promise.resolve(state[id].result);
      const epoch = generation, version = (versions.get(id) || 0) + 1;
      versions.set(id, version); update(id, { status: 'loading', error: null });
      const request = Promise.resolve().then(() => {
        if (!valid(epoch, id, version)) return null;
        return api.read(id, force);
      }).catch(async error => {
        if (!valid(epoch, id, version)) return null;
        if ((error?.response?.status || error?.status) !== 409) throw error;
        update(id, { notice: 'Already reading' });
        // One delayed saved-result read, not a second cost-bearing request.
        await new Promise(resolve => { const timer = setTimeout(() => { waits.delete(timer); resolve(); }, conflictDelayMs); waits.set(timer, resolve); });
        if (!valid(epoch, id, version)) return null;
        const saved = await api.saved([id]);
        if (!saved[id] || saved[id].status === 'pending') throw error;
        return saved[id];
      }).then(result => { if (result && valid(epoch, id, version)) accept(id, result); return result; })
        .catch(error => { if (valid(epoch, id, version)) update(id, { status: 'error', error: mediaTextError(error), retryAt: Date.now() + mediaTextRetryAfter(error) * 1000 }); })
        .finally(() => { if (valid(epoch, id, version)) { running.delete(id); update(id, { notice: null }); } });
      running.set(id, request); return request;
    },
    clear() {
      generation++; clearTimeout(batchTimer); batchTimer = null;
      waits.forEach((resolve, timer) => { clearTimeout(timer); resolve(); }); waits.clear();
      pending.clear(); checked.clear(); running.clear(); versions.clear(); state = {}; listeners.forEach(fn => fn());
    },
    dispose() { store.clear(); active = false; },
  };
  return store;
}
